import {
  FileText,
  Eye,
  RefreshCw,
  AlertCircle,
  Inbox,
  Clock,
} from "lucide-react";
import OrbitLoader from "../../../ui/components/OrbitLoader";
import useMyCase from "../useMyCase";
import {
  statusLabel,
  toneForStatus,
  formatDate,
  formatHours,
  componentLabel,
  activityDisplay,
  activityWindow,
} from "../caseFormat";

// DHW brand palette (see project requirements doc). Marigold is an accent
// only, used sparingly, never for large fills.
const DHW_BLUE = "#001489";
const DHW_JADE = "#007864";

const StatusPill = ({status}) => {
  const label = statusLabel(status);
  if (!label) return null;
  const tone = toneForStatus(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold"
      style={{backgroundColor: tone.bg, color: tone.text}}
    >
      <span
        aria-hidden="true"
        className="h-2 w-2 rounded-full"
        style={{backgroundColor: tone.dot}}
      />
      {label}
    </span>
  );
};

// One task, one plain number: the weekly goal the CM set
// (anticipated_hrs_per_week). Reported/"actual" hours were removed 08-12: a
// prod census showed 96% of activity rows are never edited after creation,
// so the CRM's actual_hrs_per_week is a stale creation-week snapshot, not
// recent history. No monthly meter either — the only running hours ledger
// lives on DHW's side and is not readable.
// Appointment-style entries (case-management meetings, OTH_CMT) legitimately
// carry no weekly hours, so they skip the ask-your-CM fallback line.
const ActivityRow = ({activity}) => {
  const goal = activity.planned_hours_per_week;
  const hasGoal = goal !== null && goal !== undefined;
  const isAppointment = String(activity.activity_code || "").startsWith(
    "OTH_CMT",
  );
  // Plain-language title from the DHW code; the CM's own wording renders as
  // a small note only when it adds real information (never codes/IDs/names).
  const {title, note} = activityDisplay(activity);
  const isFuture = activityWindow(activity) === "future";
  return (
    <li className="rounded-xl bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-[#191919]">{title}</span>
        {/* Silence means "doing this now" (Marco 08-17): with
            replaced/stopped/finished rows hidden, everything visible is
            status-active by construction, so a pill renders only as an
            exception — "Upcoming" for a phase that hasn't started, or an
            unusual status like "Not started". */}
        {isFuture ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold"
            style={{backgroundColor: "#FFF3E3", color: "#9A5300"}}
          >
            <span
              aria-hidden="true"
              className="h-2 w-2 rounded-full"
              style={{backgroundColor: "#FF9425"}}
            />
            Upcoming
          </span>
        ) : (
          activity.activity_status &&
          activity.activity_status !== "AC" && (
            <StatusPill status={activity.activity_status} />
          )
        )}
      </div>
      {note && <p className="mt-0.5 text-xs text-slate-400">{note}</p>}
      {/* Every row shows its date window (Marco 08-17): phased plans make
          "which period is this row?" the first question a reviewer asks. */}
      {(() => {
        const begin = formatDate(activity.begin_date);
        const endOk =
          !activity.begin_date ||
          !activity.end_date ||
          String(activity.end_date) >= String(activity.begin_date);
        const end = endOk ? formatDate(activity.end_date) : null;
        if (!begin && !end) return null;
        const range =
          begin && end
            ? `${begin} to ${end}`
            : begin
              ? `From ${begin}`
              : `Until ${end}`;
        return <p className="mt-0.5 text-xs text-slate-400">{range}</p>;
      })()}
      {/* A FUTURE phase announces itself without a "Goal: do" line so the
          plan-total banner (which sums exactly the visible "Goal: do"
          lines) stays reconcilable on sight. */}
      {isFuture && hasGoal ? (
        <p className="mt-1 text-sm text-slate-600">
          Starts {formatDate(activity.begin_date)}:{" "}
          <strong>{formatHours(goal)}</strong> each week from then.
        </p>
      ) : hasGoal ? (
        <p className="mt-1 text-sm text-slate-600">
          Goal: do <strong>{formatHours(goal)}</strong> each week.
        </p>
      ) : (
        !isAppointment && (
          <p className="mt-1 text-sm text-slate-600">
            Ask your case manager how many hours to plan for this activity.
          </p>
        )
      )}
    </li>
  );
};

// One rule (Marco 08-16, superseding the 08-15 CP carve-out): the plan view
// shows ONLY what the participant currently has to do. Replaced (RE),
// stopped (DS), and completed (CP) rows are all hidden — a finished row in a
// to-do list is history in the wrong place, and its goal line broke the
// visible arithmetic against the Active-only total. CP data stays in the
// payload for a future "your progress" view.
const isVisibleActivity = (a) =>
  !["RE", "DS", "CP"].includes(a.activity_status) &&
  // A row whose date window already ENDED is a finished phase (CMs build
  // phased plans: 40h/wk for two weeks, then 8h/wk after) — history, not a
  // current ask, even while its status is still Active.
  activityWindow(a) !== "expired";

// One status per task: the pill lives on the ACTIVITY rows only. A second
// pill up here duplicated the same "Active" twice per task and read as
// noise (Marco, 07-31). The group header stays a plain label.
const ComponentBlock = ({component}) => {
  const visible = component.activities.filter(isVisibleActivity);
  // A component whose every activity was replaced/stopped disappears with
  // them; "No activities added yet" is only for genuinely empty groups.
  if (component.activities.length > 0 && visible.length === 0) return null;
  return (
    <div className="rounded-2xl bg-slate-50 p-4">
      <h5 className="mb-2 text-sm font-bold text-[#191919]">
        {componentLabel(component)}
      </h5>
      {visible.length === 0 ? (
        <p className="text-sm text-slate-600">No activities added yet.</p>
      ) : (
        <ul className="space-y-2">
          {visible.map((a, i) => (
            <ActivityRow key={a.activity_id || i} activity={a} />
          ))}
        </ul>
      )}
    </div>
  );
};

const WorkPlan = ({plan, coveredByLabel}) => {
  if (!plan) {
    // Policy: ONE work plan ever, on the program-of-engagement family
    // (DECISIONS.md 2026-08-10 + reference/multiple-referrals-hierarchy.md).
    // When the other family holds the plan, say this case is covered —
    // "your CM will add it soon" would promise a second plan that never
    // comes. Policy word is MET/covered, never "hold/inactive/excused".
    return (
      <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
        {coveredByLabel
          ? `This case is open and covered by your ${coveredByLabel} case. Nothing extra is needed.`
          : "No work plan yet. Your case manager will add it soon."}
      </p>
    );
  }
  const begin = formatDate(plan.begin_date);
  // Real CRM data holds reversed ranges (end typed before begin, e.g. a
  // 2027 end entered as 2026) — showing "Aug 3, 2026 to Feb 10, 2026" reads
  // as broken, so an end date earlier than the begin date is dropped.
  const endIsValid =
    !plan.begin_date ||
    !plan.end_date ||
    String(plan.end_date) >= String(plan.begin_date);
  const end = endIsValid ? formatDate(plan.end_date) : null;
  // Real CM data often skips link fields, so activities can arrive outside
  // any component group (`other_activities`, 46% of prod activities). They
  // still carry the hours that go to DHW, so they render as their own group.
  const otherActivities = (plan.other_activities ?? []).filter(
    isVisibleActivity,
  );
  const components = plan.components ?? [];
  // A component RENDERS when it is genuinely empty (shows its own "No
  // activities added yet") or still has a visible activity; a component
  // whose every activity is hidden vanishes with them. hasContent must count
  // renderable components, or an all-finished plan paints a blank section.
  const renderableComponents = components.filter(
    (c) => c.activities.length === 0 || c.activities.some(isVisibleActivity),
  );
  const hasContent =
    renderableComponents.length > 0 || otherActivities.length > 0;
  const anyActivitiesAnywhere =
    components.some((c) => c.activities.length > 0) ||
    (plan.other_activities ?? []).length > 0;
  // The one number people ask first: "how much do I need to do?" Invariant
  // (Marco 08-16/17): the banner sums EXACTLY the goal lines the user can
  // see. Hidden statuses (RE/DS/CP) are out because their rows are out;
  // blank/Not-started rows count because their goal lines render — an
  // on-screen goal missing from the total is the 23-vs-31 bug again.
  const allActivities = [
    ...components.flatMap((c) => c.activities).filter(isVisibleActivity),
    ...otherActivities,
  ];
  const weeklyGoal = allActivities.reduce(
    (sum, a) =>
      sum +
      (activityWindow(a) === "current" &&
      Number.isFinite(Number(a.planned_hours_per_week))
        ? Number(a.planned_hours_per_week)
        : 0),
    0,
  );
  return (
    <div>
      {(begin || end) && (
        <p className="mb-3 text-sm text-slate-500">
          Plan dates: {begin && end ? `${begin} to ${end}` : begin || end}
        </p>
      )}
      {/* Sanity cap (PLAN-portal-data-accuracy): a visible-lines sum above 60
          hrs/week is the overlapping-components CRM bug, not a real ask.
          Blank-state the banner rather than show a wrong number; the activity
          rows still render their own goals. */}
      {weeklyGoal > 0 && weeklyGoal <= 60 && (
        <p
          className="mb-3 rounded-xl px-4 py-3 text-sm font-semibold"
          style={{backgroundColor: "#EEF1FB", color: DHW_BLUE}}
        >
          Altogether, your plan asks for about {formatHours(weeklyGoal)} each
          week.
        </p>
      )}
      {!hasContent ? (
        <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
          {anyActivitiesAnywhere
            ? "Nothing in your plan is active right now. Your case manager will update it."
            : "No activities have been added yet. Your case manager will add these."}
        </p>
      ) : (
        <div className="space-y-3">
          {renderableComponents.map((c, i) => (
            <ComponentBlock key={c.component_id || i} component={c} />
          ))}
          {otherActivities.length > 0 && (
            <div className="rounded-2xl bg-slate-50 p-4">
              <h5 className="mb-2 text-sm font-bold text-[#191919]">
                {renderableComponents.length > 0
                  ? "Other activities"
                  : "Your activities"}
              </h5>
              <ul className="space-y-2">
                {otherActivities.map((a, i) => (
                  <ActivityRow key={a.activity_id || i} activity={a} />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Presentation-only collapse of referrals into program FAMILIES
// (DECISIONS.md 2026-08-10): all SNAP-family referrals (ABAWD/JSAP) render as
// ONE "SNAP Employment & Training" section; TANF (incl. EWS) is its own
// section. The acronyms ABAWD/JSAP/EWS are never shown — they fail the
// ≤8th-grade plain-language requirement and DHW's model is one engagement,
// one plan, one case manager per person. Per-referral fidelity stays in the
// API payload; this grouping is display-only.
//
// Referrals arrive from the backend in engagement-rigor order, so the first
// family is the program of engagement: it gets the full card (plan + CM).
const familyOf = (referral) =>
  referral.benefit_program_code === "TANF" || referral.referral_type === "EWS"
    ? "TANF"
    : "SNAP";

// Rigor order: the engagement referral (EWS, TANF family) first, then ABAWD,
// then JSAP. The backend returns referrals in CRM order, NOT this order, so
// sort here: with SNAP first, a SNAP plan was shown under the TANF chip with
// "This plan also covers your TANF case" (QA 10-05).
const RIGOR = ["EWS", "TANF", "ABAWD", "JSAP"];
const rigorOf = (referral) => {
  const byType = RIGOR.indexOf(referral.referral_type);
  if (byType !== -1) return byType;
  return familyOf(referral) === "TANF" ? 1 : RIGOR.length;
};

const buildFamilies = (referrals) => {
  const byName = new Map();
  const ordered = referrals
    .map((r, i) => ({r, i}))
    .sort((a, b) => rigorOf(a.r) - rigorOf(b.r) || a.i - b.i)
    .map(({r}) => r);
  for (const r of ordered) {
    const name = familyOf(r);
    if (!byName.has(name)) byName.set(name, {name, referrals: []});
    byName.get(name).referrals.push(r);
  }
  // Insertion order = rigor order (sorted above), so the first family holds
  // the engagement referral. Within a family the first
  // referral with a plan wins (rigor order prefers the ABAWD-linked plan
  // over a JSAP-linked one, per the decision).
  return [...byName.values()].map((fam) => {
    const planReferral = fam.referrals.find((r) => r.work_plan) ?? null;
    return {
      ...fam,
      plan: planReferral?.work_plan ?? null,
      // Acceptance belongs to the referral that holds the plan — a sibling
      // referral's acknowledgement says nothing about THIS plan.
      planAcceptedDate:
        (planReferral?.plan_accepted && planReferral?.plan_accepted_date) ||
        null,
      caseManager:
        fam.referrals.map((r) => r.case_manager_name).find(Boolean) ?? null,
      caseNumbers: [
        ...new Set(fam.referrals.map((r) => r.case_number).filter(Boolean)),
      ],
    };
  });
};

// ONE card for the whole person (Marco 08-17, supersedes the 08-10
// two-section layout; rationale in DECISIONS.md): the primary program's chip
// + "Your Work Plan", with any other open case acknowledged in a single
// coverage line instead of a boilerplate second card.
// No case-level status pill: real open cases are 97.5% "NEW" (wallpaper)
// and otherwise "REO" (raw jargon); lifecycle codes are internal vocabulary.
const PlanCard = ({primary, coverageLines}) => (
  <article className="rounded-[2rem] border border-[#ECECEC] bg-white p-5 shadow-sm md:p-7">
    <div className="mb-5 flex flex-col gap-3 border-b border-slate-100 pb-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white"
          style={{backgroundColor: DHW_BLUE}}
          aria-hidden="true"
        >
          <FileText size={22} />
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="rounded-full px-2.5 py-0.5 text-xs font-black uppercase tracking-wide text-white"
              style={{backgroundColor: DHW_JADE}}
            >
              {primary.name}
            </span>
            <h3 className="text-lg font-bold text-[#191919]">Your Work Plan</h3>
          </div>
          <p className="mt-0.5 text-sm text-slate-500">
            Case number:{" "}
            {primary.caseNumbers.length > 0
              ? primary.caseNumbers.join(", ")
              : "Not available"}
          </p>
          {primary.caseManager && (
            <p className="mt-0.5 text-sm text-slate-500">
              Your case manager: {primary.caseManager}
            </p>
          )}
          {/* Only when accepted (backend guards flag + sane date). No
              "not accepted yet" line — that reads as blame on a screen the
              participant can't act on. */}
          {primary.planAcceptedDate && formatDate(primary.planAcceptedDate) && (
            <p
              className="mt-0.5 text-sm font-semibold"
              style={{color: DHW_JADE}}
            >
              Plan accepted {formatDate(primary.planAcceptedDate)}
            </p>
          )}
        </div>
      </div>
    </div>

    <WorkPlan plan={primary.plan} coveredByLabel={null} />
    {coverageLines.map((line, i) => (
      <p
        key={i}
        className="mt-4 rounded-xl px-4 py-3 text-sm"
        style={{backgroundColor: "#EEF1FB", color: "#2B2F3A"}}
      >
        {line}
      </p>
    ))}
  </article>
);

// Running monthly hours counter (Marco 08-24). Server-side sum of weekly
// check-in rows for the current calendar month; null = nothing on file, which
// is the COMMON case until the check-in pipeline is fixed (pre-filled
// anticipated hours, overwritten weekly series — see
// PLAN-portal-data-accuracy-2026-08-24.md for what must hold before this
// number is trustworthy; the backend's trust-mode env var narrows what counts
// without a UI change). "Confirmed" is the CM's word for reviewed hours; the
// blank state never shows a zero, because 0 reads as "you did nothing".
export const HoursThisMonthCard = ({hours}) => {
  const monthName =
    hours?.month && /^\d{4}-\d{2}$/.test(hours.month)
      ? new Date(
          Number(hours.month.slice(0, 4)),
          Number(hours.month.slice(5)) - 1,
          15,
        ).toLocaleDateString("en-US", {month: "long"})
      : null;
  const updated = hours?.last_updated
    ? formatDate(String(hours.last_updated).slice(0, 10))
    : null;
  return (
    <article className="mb-4 rounded-[2rem] border border-[#ECECEC] bg-white p-5 shadow-sm md:p-7">
      <div className="flex items-center gap-3">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white"
          style={{backgroundColor: DHW_JADE}}
          aria-hidden="true"
        >
          <Clock size={22} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#191919]">
            Your hours{monthName ? ` in ${monthName}` : " this month"}
          </h3>
          {hours ? (
            <>
              <p
                className="mt-0.5 text-2xl font-black"
                style={{color: DHW_JADE}}
              >
                {formatHours(hours.hours)}
              </p>
              <p className="mt-0.5 text-sm text-slate-500">
                Confirmed so far this month.
                {updated ? ` Last updated ${updated}.` : ""}
              </p>
            </>
          ) : (
            <p className="mt-0.5 text-sm text-slate-600">
              No hours are on file yet this month. Hours show up here after your
              weekly check-in with your case manager.
            </p>
          )}
        </div>
      </div>
    </article>
  );
};

// The whole family-card section for one participant's referral list.
// Exported so the /gallery design-review page renders the EXACT same
// presentation over fixture data.
export const CaseFamilies = ({referrals}) => {
  const families = buildFamilies(referrals);
  // Primary = the family with the plan (there is only ever one real plan);
  // with no plan anywhere, the engagement family (first in rigor order).
  const primary = families.find((f) => f.plan) ?? families[0];
  const isEngagement = primary === families[0];
  const others = families.filter((f) => f !== primary);
  // Merge identity across the whole person: one card speaks for all cases.
  const merged = {
    ...primary,
    // Program Type is the program of RECORD, not a list: a participant in
    // both families reads as TANF (09-15 ticket, reversing the 08-17
    // "TANF/SNAP" chip and matching the same precedence the orientation
    // videos now use). What the 08-17 decision was protecting -- the other
    // case not looking absent -- is carried by the coverage line below,
    // which still names it, and by caseNumbers, which still lists both.
    name: families.some((f) => f.name === "TANF") ? "TANF" : families[0].name,
    caseNumbers: [...new Set(families.flatMap((f) => f.caseNumbers))],
    caseManager: families.map((f) => f.caseManager).find(Boolean) ?? null,
  };
  const coverageLines = others.map((o) => {
    // Coverage flows DOWN the hierarchy: the engagement family's plan covers
    // lesser cases. A plan sitting on a LOWER family (made before the higher
    // referral arrived) covers nothing above it — that case's plan is still
    // coming, and saying otherwise invites skipping the planning meeting.
    if (!primary.plan)
      return `One plan will cover your ${primary.name} and ${o.name} cases.`;
    if (isEngagement)
      return `This plan also covers your ${o.name} case. Nothing extra is needed.`;
    return `You also have a ${o.name} case. Your case manager will set up its plan with you.`;
  });
  return <PlanCard primary={merged} coverageLines={coverageLines} />;
};

const MyCaseDashboard = ({loggedInUser}) => {
  const {status, caseData, refetch} = useMyCase(loggedInUser);
  const referrals = caseData?.referrals ?? [];

  return (
    <div data-testid="mycase" className="min-h-full bg-white pb-10 font-sans">
      {/* Intro sits under the app-chrome title ("My Work Plan") — don't
          repeat that heading here. */}
      <div className="mb-6">
        <p className="text-base text-slate-600">
          This is the plan you and your case manager made. It shows your
          activities and the weekly hours to aim for.
        </p>
        <div
          className="mt-4 flex items-start gap-3 rounded-2xl border p-4"
          style={{borderColor: "#CBD3F0", backgroundColor: "#EEF1FB"}}
        >
          <Eye
            size={20}
            className="mt-0.5 shrink-0"
            style={{color: DHW_BLUE}}
            aria-hidden="true"
          />
          <p className="text-sm text-slate-700">
            <span className="font-bold" style={{color: DHW_BLUE}}>
              View only.
            </span>{" "}
            You can read your plan here. To make a change, talk to your case
            manager.
          </p>
        </div>
      </div>

      {/* Loading */}
      {status === "loading" && (
        <div className="flex min-h-[40vh] items-center justify-center">
          <OrbitLoader />
        </div>
      )}

      {/* Error */}
      {status === "error" && (
        <div className="mx-auto max-w-md rounded-[2rem] border border-rose-100 bg-rose-50 p-8 text-center">
          <AlertCircle
            size={40}
            className="mx-auto mb-3 text-rose-500"
            aria-hidden="true"
          />
          <h3 className="text-lg font-bold text-[#191919]">
            We could not load your work plan
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Something went wrong. Please try again. If it keeps happening, call
            your case manager.
          </p>
          <button
            type="button"
            onClick={refetch}
            className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{backgroundColor: DHW_BLUE}}
          >
            <RefreshCw size={16} aria-hidden="true" />
            Try again
          </button>
        </div>
      )}

      {/* Success. The monthly-hours summary is participant-level (it comes
          straight from the weekly check-ins, not from any referral), so it
          renders on EVERY successful load — even when there are no open
          referrals. Only the family/work-plan cards depend on referrals;
          without them we show the "no case details" note beneath the hours. */}
      {status === "success" && (
        <>
          <HoursThisMonthCard hours={caseData?.hoursThisMonth ?? null} />
          {referrals.length > 0 ? (
            <CaseFamilies referrals={referrals} />
          ) : (
            <div className="mx-auto max-w-md rounded-[2rem] border border-slate-100 bg-slate-50 p-8 text-center">
              <Inbox
                size={40}
                className="mx-auto mb-3 text-slate-400"
                aria-hidden="true"
              />
              <h3 className="text-lg font-bold text-[#191919]">
                No case details yet
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                We do not have any case information for you right now. If you
                think this is wrong, call your case manager.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyCaseDashboard;
